import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../../components/buttons';
import { Audience } from '../../../../../models/audiences';
import { IModalContentProps } from '../../../../../models/modals';
import { ToastType } from '../../../../../models/toast';
import { useAppDispatch } from '../../../../../reducers';
import { useInitiateAudienceExtractionMutation } from '../../../../../services/endpoints/audiences';
import { showToast } from '../../../../toasts/toastsSlice';
import { useWorkspace } from '../../../../workspaces/hooks';

type ConfirmDownloadModalProps = {
  audience?: Audience;
} & IModalContentProps;

export const ConfirmDownloadModal: FC<ConfirmDownloadModalProps> = ({ audience, closeModal }) => {
  const { t } = useTranslation('audience_details');
  const workspace = useWorkspace();

  const [initiateExtraction, { isLoading, isSuccess }] = useInitiateAudienceExtractionMutation();

  const onClick = async () => {
    await initiateExtraction({
      workspaceId: workspace.id,
      audienceId: audience.id,
    });
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isSuccess) {
      closeModal();

      dispatch(
        showToast({
          type: ToastType.SUCCESS,
          title: t('sources_and_rules.audienceExtractionSuccessTitle'),
          message: t('sources_and_rules.audienceExtractionStarted', { title: audience.name }),
        })
      );
    }
  }, [isSuccess]);

  return (
    <div className='flex flex-col justify-center size-fit py-5 px-8 text-gray-900 gap-4'>
      <h1 className='py-4 text-lg font-semibold first-letter:capitalize'>{t('sources_and_rules.confirmYourDownload')}</h1>
      <p className='whitespace-pre-wrap text-sm leading-7'>{t('sources_and_rules.confirmAudienceDownloadMessage')}</p>
      <div className='flex justify-evenly *:px-[17px] *:py-[9px] *:rounded-md py-2 *:w-[164px] *:h-[38px]'>
        <Button disabled={isLoading} onClick={closeModal} variant='transparent' className='bg-gray-400 hover:bg-gray-500 text-white-100'>
          <p className='capitalize'>{t('sources_and_rules.cancel')}</p>
        </Button>
        <Button loading={isLoading} onClick={onClick}>
          <p className='first-letter:capitalize'>{t('sources_and_rules.startNow')}</p>
        </Button>
      </div>
    </div>
  );
};
